import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import type {
  ISuggestedPricesBulkUploadPayload,
  ISuggestedPricesBulkUploadResponse,
} from '../../shared/models/suggestedPrices/bulk-upload';

export interface IPagedSuggestedPricesResponse<T> {
  currentPage: number;
  pageSize: number;
  rowCount: number;
  pageCount: number;
  results: T[];
}

export interface ISuggestedPriceZone {
  id: string;
  name: string;
  regions: string[];
  provinces: string[];
  districts: string[];
}
export interface ISuggestedPriceItem {
  id: string;
  materialCode?: string;
  price: number;
  startDate: string;
  endDate: string;
  zoneId: string;
  zoneName: string;
  isDisabled: boolean;
  zone?: ISuggestedPriceZone;
  brandName?: string;
  modelName?: string;
  created?: string | null;
  modified?: string | null;
  creatorId?: string | null;
  editorId?: string | null;
  creatorFullName?: string | null;
  editorFullName?: string | null;
}

type UploadSuggestedPricesFileResponse = { tempKey: string };
type UploadSuggestedPricesFileRequest = FormData;

type GetSuggestedPricesRequest = {
  page?: number;
  pageSize?: number;
  search?: string;
} | void;

type UpdateSuggestedPriceRequest = {
  id: string;
  materialCode?: string;
  brandName?: string;
  modelName?: string;
  price: number;
  startDate: string;
  endDate: string;
  zoneId: string;
};

type UpdateSuggestedPriceResponse = ISuggestedPriceItem;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

const normalizePositiveInteger = (value: number, fallback: number) =>
  Number.isInteger(value) && value > 0 ? value : fallback;

const buildGetSuggestedPricesUrl = (page: number, pageSize: number, search?: string) => {
  const params = new URLSearchParams();
  const normalizedPage = normalizePositiveInteger(page, DEFAULT_PAGE);
  const normalizedPageSize = normalizePositiveInteger(pageSize, DEFAULT_PAGE_SIZE);

  params.set('page', String(normalizedPage));
  params.set('size', String(normalizedPageSize));

  if (search?.trim()) {
    params.set('search', search.trim());
  }

  return `/api/suggestedprices?${params.toString()}`;
};

const TAG_SUGGESTED_PRICES = 'SuggestedPrices' as const;

export const suggestedPricesApi = createApi({
  reducerPath: 'suggestedPricesApi',
  baseQuery,
  tagTypes: [TAG_SUGGESTED_PRICES],
  endpoints: (builder) => ({
    uploadSuggestedPricesFile: builder.mutation<
      UploadSuggestedPricesFileResponse,
      UploadSuggestedPricesFileRequest
    >({
      query: (formData) => ({
        url: '/api/uploader',
        method: 'POST',
        body: formData,
      }),
    }),

    bulkUploadSuggestedPrices: builder.mutation<
      ISuggestedPricesBulkUploadResponse,
      ISuggestedPricesBulkUploadPayload
    >({
      query: (payload) => ({
        url: '/api/suggestedprices/bulk-upload',
        method: 'POST',
        body: payload,
      }),
    }),

    getSuggestedPrices: builder.query<
      IPagedSuggestedPricesResponse<ISuggestedPriceItem>,
      GetSuggestedPricesRequest
    >({
      query: (args) => {
        const page = args?.page ?? DEFAULT_PAGE;
        const pageSize = args?.pageSize ?? DEFAULT_PAGE_SIZE;
        const search = args?.search;

        return {
          url: buildGetSuggestedPricesUrl(page, pageSize, search),
          method: 'GET',
        };
      },
      providesTags: [TAG_SUGGESTED_PRICES],
    }),

    getSuggestedPriceById: builder.query<ISuggestedPriceItem, string>({
      query: (id) => ({
        url: `/api/suggestedprices/${id}`,
        method: 'GET',
      }),
      providesTags: [TAG_SUGGESTED_PRICES],
    }),

    updateSuggestedPrice: builder.mutation<
      UpdateSuggestedPriceResponse,
      UpdateSuggestedPriceRequest
    >({
      query: (payload) => ({
        url: `/api/suggestedprices/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [TAG_SUGGESTED_PRICES],
    }),

    deleteSuggestedPrice: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/suggestedprices/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAG_SUGGESTED_PRICES],
    }),
  }),
});

export const {
  useUploadSuggestedPricesFileMutation,
  useBulkUploadSuggestedPricesMutation,
  useGetSuggestedPricesQuery,
  useGetSuggestedPriceByIdQuery,
  useUpdateSuggestedPriceMutation,
  useDeleteSuggestedPriceMutation,
} = suggestedPricesApi;
